import React, { useEffect, useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";
import { IoChevronDown } from "react-icons/io5";
import { coins } from "../../constants/constants";
import { GoArrowUpRight } from "react-icons/go";
import toast from "react-hot-toast";

const HashIdModal = ({
  setOpenHashIdModal,
}: {
  setOpenHashIdModal: (v: boolean) => void;
}) => {
  const [hashId, setHashId] = useState("");
  const [selectedCoin, setSelectedCoin] = useState<string>("BTC");
  const [selectedNetwork, setSelectedNetwork] = useState<string>(
    () =>
      (coins as any[]).find((c: any) => c.abbr === "BTC")?.networks?.[0]
        ?.name ?? "BTC"
  );

  const coinOptions = useMemo(() => {
    if (!Array.isArray(coins)) return [];
    return coins.map((coin: any) => ({
      label: `${coin.name} (${coin.abbr})`,
      value: coin.abbr,
      logo: coin.logo,
      networks: coin.networks ?? [],
    }));
  }, []);

  const networksForSelected = useMemo(() => {
    const match = coinOptions.find((c) => c.value === selectedCoin);
    if (!match || !match.networks?.length) return ["ERC 20"];
    return match.networks;
  }, [coinOptions, selectedCoin]);

  useEffect(() => {
    const networkNames = networksForSelected.map((n: any) =>
      typeof n === "object" && n && "name" in n ? n.name : n
    );
    const isValid = networkNames.includes(selectedNetwork);
    if (!isValid && networksForSelected.length > 0) {
      const first = networksForSelected[0];
      const firstName =
        typeof first === "object" && first && "name" in first
          ? first.name
          : first;
      setSelectedNetwork(String(firstName));
    }
  }, [selectedCoin, networksForSelected, selectedNetwork]);

  const getExplorerTxUrl = (network?: string, hash?: string): string | null => {
    if (!hash) return null;
    const net = (network || "").toLowerCase();
    if (
      net.includes("tron") ||
      net.includes("trc") ||
      net.includes("trx") ||
      net.includes("trc20") ||
      net.includes("trc-20")
    ) {
      return `https://tronscan.org/#/transaction/${hash}`;
    }
    if (
      net.includes("bsc") ||
      net.includes("binance") ||
      net.includes("bnb") ||
      net.includes("bep20") ||
      net.includes("bep-20")
    ) {
      return `https://bscscan.com/tx/${hash}`;
    }
    if (net.includes("polygon") || net.includes("matic")) {
      return `https://polygonscan.com/tx/${hash}`;
    }
    if (net.includes("arbitrum")) {
      return `https://arbiscan.io/tx/${hash}`;
    }
    if (net.includes("optimism") || net.includes("optimistic")) {
      return `https://optimistic.etherscan.io/tx/${hash}`;
    }
    if (net.includes("avalanche") || net.includes("avax")) {
      return `https://snowtrace.io/tx/${hash}`;
    }
    if (net.includes("fantom")) {
      return `https://ftmscan.com/tx/${hash}`;
    }
    if (net.includes("base")) {
      return `https://basescan.org/tx/${hash}`;
    }
    if (net.includes("linea")) {
      return `https://lineascan.build/tx/${hash}`;
    }
    if (net.includes("zksync") || net.includes("zk-sync")) {
      return `https://explorer.zksync.io/tx/${hash}`;
    }
    if (net.includes("solana") || net.includes("sol")) {
      return `https://solscan.io/tx/${hash}`;
    }
    if (net.includes("btc") || net.includes("bitcoin")) {
      return `https://btcscan.org/tx/${hash}`;
    }
    if (net.includes("doge") || net.includes("dogecoin")) {
      return `https://dogechain.info/tx/${hash}`;
    }
    if (
      net.includes("eth") ||
      net.includes("ethereum") ||
      net.includes("erc20") ||
      net.includes("erc-20")
    ) {
      return `https://etherscan.io/tx/${hash}`;
    }
    if (
      net.includes("shib") ||
      net.includes("shibcoin") ||
      net.includes("shib-coin")
    ) {
      return `https://shibascan.io/tx/${hash}`;
    }
    if (net.includes("sol") || net.includes("solana")) {
      return `https://solscan.io/tx/${hash}`;
    }
    // default to etherscan
    return `https://etherscan.io/tx/${hash}`;
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const waiting = localStorage.getItem("waitingForTransaction");

        if (waiting === "true") {
          localStorage.removeItem("waitingForTransaction");
          setHashId("");
          toast.success(
            "All set! Once your transaction is marked completed on the explorer, our system automatically picks it up. No worries—you’ll be notified shortly 🕊️",
            { duration: 5000 }
          );
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex font-manrope justify-center items-start pt-10 md:pt-16 overflow-auto pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="w-11/12 mds:w-9/12 md:w-8/12 lgss:w-2/5 xxl:w-1/3 border dark:border-[#303030] bg-white dark:bg-[#192025] rounded-xl mx-auto p-6 shadow-2xl">
        <div className="w-full flex justify-start items-center gap-4">
          <button
            onClick={() => setOpenHashIdModal(false)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition"
          >
            <BsArrowLeft className="text-black dark:text-white text-[18px]" />
          </button>
          <h4 className="text-gray-900 dark:text-gray-100 font-sora font-semibold text-[18px]">
            Find with Hash ID
          </h4>
        </div>

        <form className="w-full mt-8 ">
          {/* Hash input */}
          <label className="w-full">
            <span className="block text-gray-800 dark:text-gray-200 text-[15px] font-semibold mb-2">
              Hash ID
            </span>
            <div className="flex items-center mb-6 gap-2 h-[54px] px-3 rounded-xl border border-[#B8C5D0] dark:border-gray-700 bg-white dark:bg-[#11181f] shadow-sm">
              <FiSearch className="text-[#0B75C2] text-[18px]" />
              <input
                type="text"
                value={hashId}
                autoFocus={true}
                onChange={(e) => setHashId(e.target.value)}
                placeholder="0x..."
                className="flex-1 bg-transparent outline-none  text-[15px] text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
            </div>
          </label>

          {/* Coin select */}
          <label className="w-full ">
            <span className="block text-gray-800 dark:text-gray-200 text-[15px] font-semibold mb-2">
              Select Coin
            </span>
            <div className="relative mb-6">
              <select
                value={selectedCoin}
                onChange={(e) => setSelectedCoin(e.target.value)}
                className="appearance-none w-full h-[58px] pl-4 pr-12 rounded-xl outline-none border border-gray-300     dark:border-gray-600 bg-white dark:bg-[#11181f] text-[16px] text-gray-900 dark:text-gray-100 font-semibold shadow-sm"
              >
                {coinOptions.map((coin) => (
                  <option key={coin.value} value={coin.value}>
                    {coin.label}
                  </option>
                ))}
              </select>
              <IoChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[18px] text-gray-800 dark:text-gray-200" />
            </div>
          </label>

          {/* Network select */}
          <label className="w-full">
            <span className="block text-gray-800 dark:text-gray-200 text-[15px] font-semibold mb-2">
              Select Network
            </span>
            <div className="relative">
              <select
                value={selectedNetwork}
                onChange={(e) => setSelectedNetwork(e.target.value)}
                className="appearance-none w-full h-[58px] pl-4 pr-12 rounded-xl outline-none border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#11181f] text-[16px] text-gray-900 dark:text-gray-100 font-semibold shadow-sm"
              >
                {networksForSelected.map((net: any) => (
                  <option
                    key={net?.id || net?.name || net}
                    value={net?.name || net}
                  >
                    {net?.name || net}
                  </option>
                ))}
              </select>
              <IoChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[18px] text-gray-800 dark:text-gray-200" />
            </div>
          </label>

          <button
            type="button"
            disabled={!hashId}
            onClick={() => {
              if (!hashId) return;

              const url = getExplorerTxUrl(selectedNetwork, hashId);

              if (typeof url === "string" && url.length > 0) {
                localStorage.setItem("waitingForTransaction", "true");

                window.open(url, "_blank", "noopener,noreferrer");
              }
            }}
            className={`w-full h-[52px] mt-24 rounded-xl text-[16px] font-semibold flex items-center justify-center gap-2 transition
    ${
      !hashId
        ? "bg-gray-400 text-gray-200 cursor-not-allowed"
        : "bg-secondary_button text-gray-50 hover:opacity-90"
    }`}
          >
            Search
            <GoArrowUpRight className="text-[20px] mt-1" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default HashIdModal;
